import { ALL_TRAFFIC } from '@utils/filter';
import calcShortcutInterval from '@utils/calcShortcutInterval';
import { DATE_SHORTCUTS } from '@utils/constants';
import getGranularity from '@utils/granularity';
import { FLOW_PRESET_DEFAULT_DIMENSIONS } from '@src/utils/dimension/appDimensionHelper';
import { type FrustrationSignalMetadata } from '@src/hooks/frustrationSignal/useFrustrationSignalConfigs';
import { SUMMARY_PANEL_TYPE } from '@src/components/top-widget-panel/constants';

const calIntervalObjFromShortcut = (shortcut) => {
  const [from, to] = calcShortcutInterval(shortcut);
  return {
    shortcut,
    from,
    to,
    granularity: getGranularity({ from, to, minuteGranularity: true, overview: false, shortcut }),
  };
};

export const createFrustrationSignalPresets = ({
  metrics,
  validateDimension,
  frustrationSignalConfigMeta,
}) => {
  const frustrationSignalUseCases = Object.keys(
    frustrationSignalConfigMeta as Record<string, FrustrationSignalMetadata>,
  ).reduce(
    (signals, signalKey) => {
      const signalConfig = frustrationSignalConfigMeta[signalKey];

      // Select the 3 default metrics: count, with_user, with_session
      const selectedMetricKeys = ['count', 'with_user', 'with_session'];
      const selectedMetricIds = selectedMetricKeys
        .map((key) => signalConfig.metrics[key])
        .filter((id) => id && metrics[id]?.active);

      const signalMetrics = signalConfig.metaDefinition.metrics
        .filter(
          (it) =>
            !!it.type &&
            metrics[it.identifier]?.active &&
            it.scope !== 'service' &&
            selectedMetricIds.includes(it.identifier),
        )
        .sort((a, b) => (a.output_order ?? Infinity) - (b.output_order ?? Infinity))
        .map((it) => it.identifier);

      if (!signalMetrics.length) return signals;
      const presetConfig = {
        title: signalConfig.name,
        key: signalKey,
        config: {
          metricsChartContainer: {
            metric: signalMetrics[0],
            metrics: [...signalMetrics],
          },
          common: {
            appliedFilters: [ALL_TRAFFIC],
            interval: calIntervalObjFromShortcut(DATE_SHORTCUTS.last6Hours),
          },
          'module:trends': {
            base: {
              dimensions: validateDimension(FLOW_PRESET_DEFAULT_DIMENSIONS),
              dimensionMetrics: signalMetrics,
            },
          },
          toggles: {
            sections: {
              summary: true,
            },
            diagnostics: {
              aiAlert: false,
              manualAlert: false,
            },
          },
          summaryWidgets: {
            type: SUMMARY_PANEL_TYPE.FLOW,
            widgets: [],
          },
        },
      };

      return {
        ...signals,
        [signalKey]: presetConfig,
      };
    },
    {} as Record<string, unknown>,
  );

  return frustrationSignalUseCases;
};
